<template>
  <a-modal
    v-if="visible"
    title="Disable active profile?"
    :visible="visible"
    width="620px"
    :destroy-on-close="true"
    @cancel="closeModal"
  >
    <div class="activity-disable-modal flex flex-column gap-3">
      <p>
        This will separate the associate and disable their profile now. They will lose access to the company and the app
        immediately.
      </p>

      <a-alert
        message="This skips the 30-day grace period. Only continue if this is intentional. If this was done by mistake, the profile will need to be activated again before the associate can finish remaining tasks."
        show-icon
        type="warning"
      />

      <section class="activity-disable-modal__section">
        <p>Select the separation type and reason before disabling this profile.</p>

        <div class="grid">
          <b class="col-6">Last Work Day <span class="required-field">*</span></b>
          <div class="col-6 text-right">
            <a-date-picker
              v-model="newTermination.termination_date"
              class="w-full"
              :format="visualDateFormat"
              :disabled-date="disabledTerminationDate"
              placeholder="Enter separation date"
            />
          </div>

          <b class="col-6">Separation Type <span class="required-field">*</span></b>
          <div class="col-6 text-right">
            <a-select
              v-model="newTermination.type"
              class="w-full"
              option-label-prop="label"
              placeholder="Select separation type"
            >
              <a-select-option
                v-for="option in separationTypeOptions"
                :key="option.value"
                :value="option.value"
                :label="option.label"
              >
                <div class="activity-disable-modal__option">
                  <strong>{{ option.label }}</strong>
                  <span>{{ option.description }}</span>
                </div>
              </a-select-option>
            </a-select>
            <span v-if="selectedSeparationType" class="activity-disable-modal__hint">
              {{ selectedSeparationType.description }}
            </span>
          </div>

          <b class="col-6">Reason <span class="required-field">*</span></b>
          <div class="col-6 text-right">
            <a-textarea v-model="newTermination.reason" :autoSize="true" placeholder="Separation Reason" />
          </div>
        </div>
      </section>

      <label class="activity-disable-modal__confirmation">
        <span>Type <b>disable</b> to confirm</span>
        <a-input v-model="disableConfirmation" placeholder="disable" />
        <span v-if="disableConfirmation !== requiredDisableConfirmation" class="activity-disable-modal__helper">
          Type disable to continue.
        </span>
      </label>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Cancel</a-button>
        <a-button type="danger" :loading="loadingDisable" :disabled="submitDisabled" @click="disableAssociate">
          Disable Profile
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Alert, Button, DatePicker, Input, Modal, notification, Select } from 'ant-design-vue'
import {
  getSeparationType,
  isResignationSeparationType,
  SEPARATION_TYPES
} from '@/modules/profile/utils/separation'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: profileActions } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'a-input': Input,
    'a-modal': Modal,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-textarea': Input.TextArea
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate: {
      type: Object,
      default: () => ({})
    },
    associateId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loadingDisable: false,
      disableConfirmation: '',
      requiredDisableConfirmation: 'disable',
      newTermination: {
        type: undefined,
        reason: undefined,
        termination_date: undefined
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.prepareModal()
      }
    }
  },
  computed: {
    paymentType() {
      return this.associate?.payment_info?.type
    },
    isContractor() {
      return this.paymentType === 'Contractor'
    },
    separationTypeOptions() {
      if (this.isContractor) {
        return [SEPARATION_TYPES.dnr, SEPARATION_TYPES.break_up]
      }

      return [SEPARATION_TYPES.terminate, SEPARATION_TYPES.resign]
    },
    selectedSeparationType() {
      return getSeparationType(this.newTermination.type)
    },
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    submitDisabled() {
      return (
        this.loadingDisable ||
        !this.associate?.id ||
        this.disableConfirmation !== this.requiredDisableConfirmation ||
        !this.newTermination.termination_date ||
        !this.newTermination.type ||
        !this.newTermination.reason ||
        this.newTermination.reason.length === 0
      )
    }
  },
  methods: {
    ...profileActions(['patchAssociateDeactivate', 'postAssociateTerminate', 'postAssociateResign', 'reloadAssociate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    prepareModal() {
      this.resetModal()
      this.newTermination.type = this.separationTypeOptions[0]?.value
    },

    resetModal() {
      this.disableConfirmation = ''
      this.newTermination = {
        type: undefined,
        reason: undefined,
        termination_date: undefined
      }
    },

    closeModal() {
      if (this.loadingDisable) {
        return
      }

      this.$emit('update:visible', false)
      this.resetModal()
    },

    disabledTerminationDate(current) {
      if (this.isContractor) {
        return current && moment(current).startOf('day').isAfter(moment().startOf('day'))
      }

      return false
    },

    async disableAssociate() {
      this.loadingDisable = true

      const termination = {
        reason: this.newTermination.reason,
        termination_date: moment(this.newTermination.termination_date).utc().startOf('day').toISOString()
      }

      const payload = {
        user_id: this.associate.user_id,
        associate_number: this.associate.associate_number,
        associate_id: this.associate.id,
        termination
      }

      try {
        if (isResignationSeparationType(this.newTermination.type)) {
          await this.postAssociateResign(payload)
        } else {
          await this.postAssociateTerminate(payload)
        }

        await this.patchAssociateDeactivate({
          associate_id: this.associateId
        })

        this.showNotification('success', 'Profile disabled.', '')
        this.$emit('update:visible', false)
        this.resetModal()
        this.reloadAssociate(true)
      } catch (error) {
        this.showNotification(
          'error',
          'Profile could not be disabled. Try again or contact support if the issue continues.',
          typeof error === 'string' ? error : error?.message
        )
      } finally {
        this.loadingDisable = false
      }
    }
  }
}
</script>

<style scoped>
.ant-calendar-picker {
  margin: 0;
  min-width: unset !important;
}

.activity-disable-modal p {
  margin: 0;
}

.activity-disable-modal__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-disable-modal__confirmation {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
}

.activity-disable-modal__helper {
  color: #ef4444;
  font-size: 12px;
}

.activity-disable-modal__hint,
.activity-disable-modal__option span {
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.activity-disable-modal__hint {
  display: block;
  margin-top: 8px;
  text-align: left;
}

.activity-disable-modal__option {
  display: flex;
  flex-direction: column;
  white-space: normal;
}
</style>
